import { useState } from "react";
import { useTenant } from "../../providers/TenantProvider";
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, Building2, Users, User, Ban } from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { UsersProvider, useUsers, type User as UserType } from "./UsersProvider";
import {
  GroupsProvider,
  useGroups,
  type PermissionGroup,
  type PermissionGroupLabel,
  type GroupUser,
} from "./GroupsProvider";
import { UserFormDialog, type UserFormData } from "./UserFormDialog";
import { GroupFormDialog } from "./GroupFormDialog";
import { LABEL_CONFIG, VALID_CHILD_TYPES } from "../../components/HierarchyPicker/types";

// --- Add Group Popover ---

type AddGroupPopoverProps = {
  parentGroup: PermissionGroup;
  onSelect: (type: PermissionGroupLabel) => void;
  onClose: () => void;
};

const AddGroupPopover = ({ parentGroup, onSelect, onClose }: AddGroupPopoverProps) => {
  const validTypes = VALID_CHILD_TYPES[parentGroup.label];

  return (
    <>
      {/* Backdrop to close popover */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Popover */}
      <div className="absolute left-0 top-full mt-1 z-50 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl py-1 min-w-[140px]">
        {validTypes.map((type) => {
          const config = LABEL_CONFIG[type];
          const Icon = config.icon;
          return (
            <button
              key={type}
              onClick={() => {
                onSelect(type);
                onClose();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors"
            >
              <Icon size={14} className={config.color} />
              Add {config.displayName}
            </button>
          );
        })}
      </div>
    </>
  );
};

// --- Delete Confirmation Dialog ---

type DeleteConfirmDialogProps = {
  group: PermissionGroup;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const DeleteConfirmDialog = ({ group, isOpen, onConfirm, onCancel }: DeleteConfirmDialogProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-sm p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Delete {group.name}?</h3>
        <p className="text-sm text-zinc-400 mb-6">
          This will permanently delete this {group.label} and cannot be undone.
          {group.userCount > 0 && (
            <span className="block mt-2 text-yellow-400">
              Warning: {group.userCount} user{group.userCount > 1 ? "s have" : " has"} roles assigned to this group.
            </span>
          )}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Role hierarchy order (highest to lowest)
const ROLE_ORDER = ["Indy Admin", "Tenant Admin", "Venue Manager", "General Staff"];

// --- Group Tree Node (Left Panel) ---

type GroupTreeNodeProps = {
  group: PermissionGroup;
  level?: number;
  selectedGroupId: string | null;
  canManageGroups: boolean;
  onSelect: (group: PermissionGroup) => void;
  onEdit: (group: PermissionGroup) => void;
  onDelete: (group: PermissionGroup) => void;
  onAddChild: (parentGroup: PermissionGroup, preselectedType: PermissionGroupLabel) => void;
};

const GroupTreeNode = ({
  group,
  level = 0,
  selectedGroupId,
  canManageGroups,
  onSelect,
  onEdit,
  onDelete,
  onAddChild,
}: GroupTreeNodeProps) => {
  const { getChildGroups } = useGroups();
  const [expanded, setExpanded] = useState(true);
  const [showAddPopover, setShowAddPopover] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const children = getChildGroups(group.id);
  const hasChildren = children.length > 0;
  const config = LABEL_CONFIG[group.label];
  const Icon = config.icon;
  const isSelected = selectedGroupId === group.id;

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAddPopover(true);
  };

  const handleTypeSelect = (type: PermissionGroupLabel) => {
    onAddChild(group, type);
    setShowAddPopover(false);
  };

  const handleDeleteConfirm = () => {
    onDelete(group);
    setShowDeleteDialog(false);
  };

  // Calculate indentation based on level
  const indent = level * 32; // 32px per level

  return (
    <div className="relative">
      {/* Vertical connecting line from parent */}
      {level > 0 && <div className="absolute top-0 bottom-0 w-px bg-zinc-700" style={{ left: indent - 16 }} />}

      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(group)}
        onKeyDown={(e) => e.key === "Enter" && onSelect(group)}
        className={`group relative flex items-center justify-between py-3 px-4 border-b border-zinc-800/50 hover:bg-zinc-800/20 transition-colors cursor-pointer ${
          isSelected ? "bg-zinc-800/40" : ""
        }`}
        style={{ paddingLeft: indent + 16 }}
      >
        {/* Horizontal connecting line */}
        {level > 0 && <div className="absolute top-1/2 h-px bg-zinc-700" style={{ left: indent - 16, width: 12 }} />}

        {/* Left side: Icon + Name + User count */}
        <div className="flex items-center gap-3">
          <div className={`w-6 h-6 flex items-center justify-center rounded ${config.bgColor}`}>
            <Icon size={14} className={config.color} />
          </div>
          <span className="text-sm font-semibold text-white">{group.name}</span>
          {group.userCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-zinc-500">
              <User size={11} />
              {group.userCount}
            </span>
          )}
          {/* Expand/collapse chevron */}
          {hasChildren && (
            <button
              className="text-zinc-500 hover:text-white transition-colors p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
        </div>

        {/* Right side: Actions (visible on hover) */}
        {canManageGroups && (
          <div
            className="flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Add child button */}
            <div className="relative">
              <button
                onClick={handleAddClick}
                className="text-[10px] font-semibold text-zinc-400 hover:text-white transition-all flex items-center gap-1 uppercase tracking-wide"
              >
                <Plus size={12} />
                Add{" "}
                {(() => {
                  const firstChildType = VALID_CHILD_TYPES[group.label]?.[0];
                  return firstChildType ? LABEL_CONFIG[firstChildType].displayName : "Child";
                })()}
              </button>
              {showAddPopover && (
                <AddGroupPopover
                  parentGroup={group}
                  onSelect={handleTypeSelect}
                  onClose={() => setShowAddPopover(false)}
                />
              )}
            </div>

            {/* Edit/delete buttons with separator */}
            <div className="flex items-center gap-2 border-l border-zinc-700 pl-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(group);
                }}
                className="p-1 text-zinc-400 hover:text-blue-400 transition-colors"
                title="Edit group"
              >
                <Pencil size={14} />
              </button>
              {group.label !== "tenant" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                  className="p-1 text-zinc-400 hover:text-red-400 transition-colors"
                  title="Delete group"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Delete confirmation dialog */}
        <DeleteConfirmDialog
          group={group}
          isOpen={showDeleteDialog}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteDialog(false)}
        />
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div className="relative">
          {children.map((child) => (
            <GroupTreeNode
              key={child.id}
              group={child}
              level={level + 1}
              selectedGroupId={selectedGroupId}
              canManageGroups={canManageGroups}
              onSelect={onSelect}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- User Row (Right Panel) ---

type UserRowProps = {
  user: GroupUser;
  onEdit: (userId: string) => void;
};

const UserRow = ({ user, onEdit }: UserRowProps) => {
  return (
    <div
      onClick={() => onEdit(user.id)}
      className={`flex items-center gap-4 py-3 px-4 hover:bg-zinc-800/30 transition-colors border-b border-zinc-800/50 last:border-b-0 cursor-pointer ${
        user.disabled ? "opacity-60" : ""
      }`}
    >
      <div className="p-1.5 bg-zinc-700/50 rounded">
        <User size={16} className="text-zinc-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium truncate ${user.disabled ? "text-zinc-400" : "text-white"}`}>
            {user.firstName} {user.lastName}
          </span>
          {user.disabled && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-red-400 bg-red-500/20 rounded">
              <Ban size={10} />
              Disabled
            </span>
          )}
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">{user.email}</div>
      </div>
    </div>
  );
};

// --- Group Users Panel (Right Panel) ---

type GroupUsersPanelProps = {
  group: PermissionGroup;
  users: GroupUser[];
  loading: boolean;
  allUsers: UserType[];
  canManageUsers: boolean;
  onEditUser: (userId: string) => void;
  onAddUser: () => void;
};

const GroupUsersPanel = ({ group, users, loading, canManageUsers, onEditUser, onAddUser }: GroupUsersPanelProps) => {
  const config = LABEL_CONFIG[group.label];
  const Icon = config.icon;

  // Sort users by role hierarchy
  const sortedUsers = [...users].sort((a, b) => {
    const aIndex = ROLE_ORDER.indexOf(a.roleDisplayName);
    const bIndex = ROLE_ORDER.indexOf(b.roleDisplayName);
    if (aIndex === -1 && bIndex === -1) return a.roleDisplayName.localeCompare(b.roleDisplayName);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Group users by role
  const usersByRole = sortedUsers.reduce(
    (acc, user) => {
      const role = user.roleDisplayName;
      if (!acc[role]) acc[role] = [];
      acc[role].push(user);
      return acc;
    },
    {} as Record<string, GroupUser[]>,
  );

  const sortedRoles = Object.keys(usersByRole).sort((a, b) => {
    const aIndex = ROLE_ORDER.indexOf(a);
    const bIndex = ROLE_ORDER.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-zinc-800">
        <span
          className={`flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide rounded ${config.bgColor} ${config.color}`}
        >
          <Icon size={12} />
          {LABEL_CONFIG[group.label].displayName}
        </span>
        <h2 className="text-lg font-semibold text-white flex-1">{group.name}</h2>
        {canManageUsers && (
          <button
            onClick={onAddUser}
            className="flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-white transition-all uppercase tracking-wide"
          >
            <Plus size={14} />
            Add User
          </button>
        )}
      </div>

      {/* Users list */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="text-zinc-500 py-8 text-center text-sm">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="text-zinc-500 py-8 text-center">
            <Users size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No users have roles assigned to this group</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedRoles.map((role) => (
              <div key={role}>
                <h3 className="text-xs font-medium text-zinc-400 mb-2 px-1">{role}</h3>
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                  {usersByRole[role]?.map((user) => (
                    <UserRow key={`${user.id}-${user.roleName}`} user={user} onEdit={onEditUser} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Main Screen Content ---

const ScreenContent = () => {
  const { hasPermission, permissions } = useAuth();
  const { groups, loading, error, getRootGroups, createGroup, updateGroup, deleteGroup, fetchGroups, getUsersInGroup } =
    useGroups();
  const { users, getUserById, createUser, updateUser, assignRole, removeRoleAssignment } = useUsers();

  // Permission checks
  const canManageGroups = hasPermission("groups.manage");
  const canManageUsers = hasPermission("users.manage");
  const isPlatformSuperAdmin = permissions?.isPlatformSuperAdmin ?? false;

  const [selectedGroup, setSelectedGroup] = useState<PermissionGroup | null>(null);
  const [groupUsers, setGroupUsers] = useState<GroupUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Group form dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PermissionGroup | null>(null);
  const [parentGroup, setParentGroup] = useState<PermissionGroup | null>(null);
  const [preselectedType, setPreselectedType] = useState<PermissionGroupLabel | null>(null);

  // User form dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);

  const rootGroups = getRootGroups();

  const handleSelectGroup = async (group: PermissionGroup) => {
    setSelectedGroup(group);
    setLoadingUsers(true);
    try {
      const fetchedUsers = await getUsersInGroup(group.id);
      setGroupUsers(fetchedUsers);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setGroupUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Group handlers
  const handleCreateGroup = () => {
    setEditingGroup(null);
    setParentGroup(null);
    setGroupDialogOpen(true);
  };

  const handleEditGroup = (group: PermissionGroup) => {
    setEditingGroup(group);
    setParentGroup(null);
    setGroupDialogOpen(true);
  };

  const handleAddChildGroup = (parent: PermissionGroup, type: PermissionGroupLabel) => {
    setEditingGroup(null);
    setParentGroup(parent);
    setPreselectedType(type);
    setGroupDialogOpen(true);
  };

  const handleDeleteGroup = async (group: PermissionGroup) => {
    try {
      await deleteGroup(group.id);
      if (selectedGroup?.id === group.id) {
        setSelectedGroup(null);
        setGroupUsers([]);
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleGroupDialogSubmit = async (data: {
    name: string;
    label: PermissionGroupLabel;
    parentId: string | null;
  }) => {
    if (editingGroup) {
      await updateGroup(editingGroup.id, data);
    } else {
      await createGroup(data);
    }
  };

  // User handlers
  const handleCreateUser = () => {
    setEditingUser(null);
    setUserDialogOpen(true);
  };

  const handleEditUser = async (userId: string) => {
    // Fetch the full user on demand rather than relying on the tenant-scoped
    // `users` list, which won't contain users from other tenants (e.g. when a
    // platform admin edits a user in a different tenant).
    try {
      const user = await getUserById(userId);
      setEditingUser(user);
      setUserDialogOpen(true);
    } catch (err) {
      console.error("Failed to load user:", err);
    }
  };

  const handleUserSubmit = async (data: UserFormData) => {
    if (editingUser) {
      await updateUser(editingUser.id, {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      });
      if (data.removeRoleAssignments) {
        for (const removal of data.removeRoleAssignments) {
          await removeRoleAssignment(editingUser.id, removal.roleId, removal.groupId);
        }
      }
      if (data.addRoleAssignments) {
        for (const assignment of data.addRoleAssignments) {
          await assignRole(editingUser.id, assignment.roleId, assignment.groupId, true);
        }
      }
    } else {
      const newUser = await createUser({ ...data, password: data.password! });
      if (data.roleAssignments) {
        for (const assignment of data.roleAssignments) {
          await assignRole(newUser.id, assignment.roleId, assignment.groupId, true);
        }
      }
    }

    // Refresh users for the selected group
    if (selectedGroup) {
      const fetchedUsers = await getUsersInGroup(selectedGroup.id);
      setGroupUsers(fetchedUsers);
    }
  };

  if (loading && groups.length === 0) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-6">
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
          <p className="text-red-300">{error}</p>
          <button onClick={fetchGroups} className="mt-2 text-sm text-red-400 hover:text-red-300">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">User Management</h1>
          <p className="text-sm text-zinc-400 mt-1">Manage users, roles, and permission groups</p>
        </div>
        {isPlatformSuperAdmin && (
          <button
            onClick={handleCreateGroup}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
          >
            <Plus size={16} />
            Create Tenant
          </button>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden px-6 pb-6 gap-4">
        {/* Left: Groups tree */}
        <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg overflow-auto">
          <div className="p-2">
            {groups.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Building2 size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No groups found</p>
                {isPlatformSuperAdmin && (
                  <button
                    onClick={handleCreateGroup}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
                  >
                    <Plus size={16} />
                    Create Tenant
                  </button>
                )}
              </div>
            ) : (
              rootGroups.map((group) => (
                <GroupTreeNode
                  key={group.id}
                  group={group}
                  selectedGroupId={selectedGroup?.id ?? null}
                  canManageGroups={canManageGroups}
                  onSelect={handleSelectGroup}
                  onEdit={handleEditGroup}
                  onDelete={handleDeleteGroup}
                  onAddChild={handleAddChildGroup}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: Selected group's users */}
        <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          {selectedGroup ? (
            <GroupUsersPanel
              group={selectedGroup}
              users={groupUsers}
              loading={loadingUsers}
              allUsers={users}
              canManageUsers={canManageUsers}
              onEditUser={handleEditUser}
              onAddUser={handleCreateUser}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center h-full text-zinc-500">
              <div className="text-center">
                <Users size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a group to view users</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Group Dialog */}
      <GroupFormDialog
        group={editingGroup}
        parentGroup={parentGroup}
        preselectedType={preselectedType}
        isOpen={groupDialogOpen}
        onClose={() => {
          setGroupDialogOpen(false);
          setEditingGroup(null);
          setParentGroup(null);
          setPreselectedType(null);
        }}
        onSubmit={handleGroupDialogSubmit}
        availableParents={groups}
      />

      {/* User Dialog */}
      <UserFormDialog
        user={editingUser}
        defaultGroupId={!editingUser ? selectedGroup?.id : null}
        isOpen={userDialogOpen}
        onClose={() => {
          setUserDialogOpen(false);
          setEditingUser(null);
        }}
        onSubmit={handleUserSubmit}
      />
    </div>
  );
};

// --- Main Screen ---

export default () => {
  const { currentTenantId } = useTenant();
  // Remount the whole subtree when the tenant changes so providers refetch and local selection resets
  return (
    <UsersProvider key={currentTenantId ?? "no-tenant"}>
      <GroupsProvider>
        <ScreenContent />
      </GroupsProvider>
    </UsersProvider>
  );
};
